/**
 * Parses OpenSearch aggregation responses into flat table rows.
 *
 * Handles nested terms aggregation buckets and extracts metric sub-aggregation values.
 * Converts the tree structure back into a flat array of row objects suitable for table rendering.
 *
 * Example input (response.aggregations):
 * {
 *   "group_by_company": {
 *     "buckets": [
 *       {
 *         "key": "Acme Corp",
 *         "doc_count": 42,
 *         "group_by_status": {
 *           "buckets": [
 *             {
 *               "key": "active",
 *               "doc_count": 30,
 *               "agg_amount": { "value": 15000 },
 *               "agg_name": { "value": 30 }
 *             }
 *           ]
 *         }
 *       }
 *     ]
 *   }
 * }
 *
 * Example output:
 * [
 *   { company: "Acme Corp", status: "active", amount: 15000, name: 30 }
 * ]
 */

import type { AggregationType, ColumnAggregationMap } from './aggregation-types';
import { COMPOSITE_AGG_NAME } from './aggregation-query-builder';
import type { SortEntry } from './sort-helpers';

interface AggBucket {
  key: string | number;
  key_as_string?: string;
  keyAsString?: string;
  doc_count: number;
  docCount?: number;
  [subAggName: string]: unknown;
}

interface AggResult {
  buckets?: AggBucket[];
  value?: number | null;
  [key: string]: unknown;
}

/**
 * Convert a snake_case agg name to camelCase to match backend's deep_camelize_keys.
 * e.g., "group_by_employeeNameKeyword" → "groupByEmployeeNameKeyword"
 *
 * Digits count as segment starts too — Rails camelize turns "agg_m_9b2c" into
 * "aggM9b2c", so the fallback must drop the underscore before a digit as well.
 */
function snakeToCamel(str: string): string {
  return str.replace(/_([a-zA-Z0-9])/g, (_, ch: string) => ch.toUpperCase());
}

/** Find an aggregation result by name, trying both snake_case and camelCase keys */
function findAgg(obj: Record<string, unknown>, snakeName: string): AggResult | undefined {
  return (obj[snakeName] ?? obj[snakeToCamel(snakeName)]) as AggResult | undefined;
}

/**
 * Resolve a metric column's value for a bucket. Only a plain `count`
 * aggregation maps to the bucket's `doc_count` when its own value is absent;
 * every other aggregation (sum/avg/min/max/cardinality) keeps its value
 * verbatim, and a null result stays null. Without this gate a null metric —
 * e.g. MAX over a column that is NULL across the whole group — would fall
 * through to doc_count and, for a date column, render that count as a bogus
 * epoch date (12-31-1969). A value of 0 is a real result and is preserved.
 */
function metricValue(
  metricAgg: AggResult | undefined,
  aggType: AggregationType,
  bucket: { doc_count?: number; docCount?: number }
): unknown {
  const value = metricAgg?.value ?? null;
  if (value !== null) return value;
  return aggType === 'count' ? (bucket.doc_count ?? bucket.docCount ?? null) : null;
}

/**
 * Flatten nested aggregation buckets into flat row objects.
 *
 * @param aggregations - The `aggregations` object from the OpenSearch response
 * @param groupByKeys - ordered array of group-by field keys
 * @param columnAggregations - map of columnKey → AggregationType
 * @returns Array of flat row objects with group-by keys and metric values
 */
export function flattenAggBuckets(
  aggregations: Record<string, unknown>,
  groupByKeys: string[],
  columnAggregations: ColumnAggregationMap
): Record<string, unknown>[] {
  if (!aggregations || groupByKeys.length === 0) return [];

  const rows: Record<string, unknown>[] = [];
  const firstKey = groupByKeys[0]!;
  const topAgg = findAgg(aggregations, `group_by_${firstKey}`);

  if (!topAgg?.buckets) return [];

  recurseFlatten(topAgg.buckets, groupByKeys, 0, {}, columnAggregations, rows);
  return rows;
}

/**
 * Recursively walk nested buckets, building row objects.
 */
function recurseFlatten(
  buckets: AggBucket[],
  groupByKeys: string[],
  depth: number,
  parentRow: Record<string, unknown>,
  columnAggregations: ColumnAggregationMap,
  rows: Record<string, unknown>[]
): void {
  const fieldKey = groupByKeys[depth]!;

  for (const bucket of buckets) {
    const row = {
      ...parentRow,
      [fieldKey]: bucket.key_as_string ?? bucket.keyAsString ?? bucket.key,
    };

    const nextDepth = depth + 1;

    if (nextDepth < groupByKeys.length) {
      // There are more group-by levels — recurse into the next nested agg
      const nextKey = groupByKeys[nextDepth]!;
      const nextAgg = findAgg(bucket as Record<string, unknown>, `group_by_${nextKey}`);

      if (nextAgg?.buckets) {
        recurseFlatten(nextAgg.buckets, groupByKeys, nextDepth, row, columnAggregations, rows);
      }
    } else {
      // Innermost level — extract metric sub-aggregation values
      for (const [colKey, aggType] of Object.entries(columnAggregations)) {
        // Skip group-by fields (they're already in the row from bucket keys)
        if (groupByKeys.includes(colKey)) continue;

        const metricAgg = findAgg(bucket as Record<string, unknown>, `agg_${colKey}`);
        row[colKey] = metricValue(metricAgg, aggType, bucket);
      }

      rows.push(row);
    }
  }
}

interface CompositeBucket {
  key: Record<string, unknown>;
  doc_count?: number;
  docCount?: number;
  [subAggName: string]: unknown;
}

interface CompositeAggResult {
  buckets?: CompositeBucket[];
  after_key?: Record<string, unknown>;
  afterKey?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * Flatten a COMPOSITE aggregation response into flat table rows.
 *
 * Composite returns a single flat list of buckets (no nested tree): each
 * bucket's `key` is a compound object keyed by source name (`group_by_<field>`),
 * with the metric sub-aggs alongside. Returns the rows plus the `after_key` for
 * the next page (undefined once the last page is reached).
 *
 * The response is camelCased by the API (deep_camelize_keys), so source names
 * and `after_key` arrive camelCased. We read them defensively and rebuild the
 * returned `afterKey` with the raw snake_case source names the composite query
 * expects when it is sent back as `after`.
 */
export function flattenCompositeBuckets(
  aggregations: Record<string, unknown>,
  groupByKeys: string[],
  columnAggregations: ColumnAggregationMap
): { rows: Record<string, unknown>[]; afterKey?: Record<string, unknown> } {
  if (!aggregations || groupByKeys.length === 0) return { rows: [] };

  const agg = findAgg(aggregations, COMPOSITE_AGG_NAME) as CompositeAggResult | undefined;
  if (!agg?.buckets) return { rows: [] };

  const rows: Record<string, unknown>[] = [];
  for (const bucket of agg.buckets) {
    const key = bucket.key ?? {};
    const row: Record<string, unknown> = {};

    for (const fieldKey of groupByKeys) {
      const sourceName = `group_by_${fieldKey}`;
      row[fieldKey] = key[sourceName] ?? key[snakeToCamel(sourceName)];
    }

    for (const [colKey, aggType] of Object.entries(columnAggregations)) {
      // Skip group-by fields — they're already in the row from the composite key
      if (groupByKeys.includes(colKey)) continue;

      const metricAgg = findAgg(bucket as Record<string, unknown>, `agg_${colKey}`);
      row[colKey] = metricValue(metricAgg, aggType, bucket);
    }

    rows.push(row);
  }

  const rawAfter = (agg.after_key ?? agg.afterKey) as Record<string, unknown> | undefined;
  let afterKey: Record<string, unknown> | undefined;
  if (rawAfter && rows.length > 0) {
    afterKey = {};
    for (const fieldKey of groupByKeys) {
      const sourceName = `group_by_${fieldKey}`;
      afterKey[sourceName] = rawAfter[sourceName] ?? rawAfter[snakeToCamel(sourceName)];
    }
  }

  return { rows, afterKey };
}

function compareRowValues(a: unknown, b: unknown): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}

/**
 * Client-side multi-key sort for aggregated rows.
 *
 * A composite aggregation can only order by its group-by source keys
 * server-side, never by a metric. So metric-column sorts (and multi-key sorts
 * generally) are applied here, on the rows already fetched for the preview.
 * Because the preview fetches a bounded window of groups, a metric sort ranks
 * within that window ("top N of many"), not globally. Nulls sort last regardless
 * of direction.
 */
export function sortAggregationRows(
  rows: Record<string, unknown>[],
  sorts: SortEntry[]
): Record<string, unknown>[] {
  if (!sorts || sorts.length === 0) return rows;
  return [...rows].sort((a, b) => {
    for (const sort of sorts) {
      const av = a[sort.key];
      const bv = b[sort.key];
      // Nulls always sort last, independent of the sort direction.
      const aNull = av == null;
      const bNull = bv == null;
      if (aNull || bNull) {
        if (aNull && bNull) continue;
        return aNull ? 1 : -1;
      }
      const cmp = compareRowValues(av, bv);
      if (cmp !== 0) return sort.direction === 'desc' ? -cmp : cmp;
    }
    return 0;
  });
}
